import type {DesignerStateType} from '@packages/systems/core/records/DesignerState';
import {type NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {
  Component,
  Expressions,
  Plugins,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {getCurrentComponentType} from '@packages/utilities/core-elements';
import {getRootElementWithFocusedInstancesExpanded} from '@packages/utilities/platform-selectors';
import {
  removeElementById,
  updateOneOfComponents,
} from '@packages/utilities/platform';
import {memoize, weakMemo} from '@packages/utilities/memo';
import {
  applySlotDefaultInsertion,
  getEffectiveSlotDefaultExpression,
  getTargetedSlotFieldId,
  graftSlotContentIntoRender,
  prepareSlotDefaultMoveCommit,
  promoteSlotDefaultMove,
  resolveSlotDefaultPathContextInFocus,
  setComponentSlotDefaultExpression,
  type PromotedSlotDefaultMove,
} from '@packages/systems/component-slots';
import {handleElementMoved, isSlotDefaultDrop} from '../DragDropUtils';
import {isInFocusedRender} from './reads';

// Reads live next door so the generic drag/drop layer can import them without
// dragging in this file, which depends on the mover.
export * from './reads';

type ElementMoveDetails = Parameters<typeof handleElementMoved>[0];
type ElementMoveContext = Parameters<typeof handleElementMoved>[1];
type ElementMoveOverrides = NonNullable<
  Parameters<typeof handleElementMoved>[2]
>;

/**
 * What the caller wants placed where. Ids, paths and a position, never promotion
 * instructions, so drag/drop never learns how a Slot default gets forked.
 *
 * Dropping a new Div after an inherited heading inside a Card instance:
 *   source:      {kind: 'new', element: <Div>}
 *   destination: {id: 'heading-1', nativeIdPath: ['card-inst', 'heading-1'],
 *                 path: null, position: 'after'}
 */
export type ProjectedPlacementIntent = {
  source:
    | {
        kind: 'existing';
        id: string;
        nativeIdPath: NativeId[] | null;
      }
    | {
        kind: 'new';
        element: ExpressionElement;
      };
  destination: {
    id: string;
    nativeIdPath: NativeId[] | null;
    path: ExpressionPath | null;
    position: ElementMoveDetails['position'];
  };
};

type ProjectedMoveInput = {
  designerState: DesignerStateType;
  sourceElementId: string;
  sourceNativeIdPath: NativeId[] | null;
  anchorElementId: string;
  anchorNativeIdPath: NativeId[] | null;
  anchorPath: ExpressionPath | null;
  anchorPosition: ElementMoveDetails['position'];
  focusedSymbolNativeIdPath: NativeId[];
};

type SlotDefaultMovePreparer = typeof promoteSlotDefaultMove;

type InternalProjectedMovePromotion =
  | {kind: 'ordinary'}
  | {kind: 'blocked'}
  | {
      kind: 'promoted';
      promotion: PromotedSlotDefaultMove;
      designerState: DesignerStateType;
    };

const getProjectedMovePromotion = (
  {
    designerState,
    sourceElementId,
    sourceNativeIdPath,
    anchorElementId,
    anchorNativeIdPath,
    anchorPath,
    anchorPosition,
    focusedSymbolNativeIdPath,
  }: ProjectedMoveInput,
  prepareMove: SlotDefaultMovePreparer = promoteSlotDefaultMove
): InternalProjectedMovePromotion => {
  const isProjectedMove = Boolean(
    (sourceNativeIdPath &&
      resolveSlotDefaultPathContextInFocus(
        sourceNativeIdPath,
        focusedSymbolNativeIdPath,
        designerState.components,
        designerState.plugins
      )) ||
    (anchorNativeIdPath &&
      !isInFocusedRender(
        anchorNativeIdPath,
        focusedSymbolNativeIdPath,
        designerState
      ) &&
      resolveSlotDefaultPathContextInFocus(
        anchorNativeIdPath,
        focusedSymbolNativeIdPath,
        designerState.components,
        designerState.plugins,
        anchorPath
      ))
  );
  if (!isProjectedMove) return {kind: 'ordinary'};

  let editingComponentType: Plugins.QualifiedElement | null = null;
  if (focusedSymbolNativeIdPath.length) {
    editingComponentType = getCurrentComponentType(
      designerState.components,
      focusedSymbolNativeIdPath
    );
  }

  const promotion = prepareMove({
    componentMap: designerState.components,
    plugins: designerState.plugins,
    sourceElementId,
    sourceNativeIdPath,
    anchorElementId,
    anchorNativeIdPath,
    anchorPath,
    anchorPosition,
    editingComponentType,
  });
  if (!promotion) return {kind: 'blocked'};
  // The promoted map is ephemeral: the Slot default forked into a real override
  // with the source and anchor reminted. It gets published whole or thrown away,
  // never merged in piecemeal.
  //
  // It forks from committed components, so a staged preview left over from an
  // earlier frame would describe a tree that no longer exists.
  return {
    kind: 'promoted',
    promotion,
    designerState: designerState
      .set('components', promotion.componentMap)
      .set('stagedComponents', null),
  };
};

type PreparedProjectedMoveValidation = {
  kind: 'prepared';
  usesProjectedTree: boolean;
  designerState: DesignerStateType;
  root: Expression;
  sourceElement: ExpressionElement;
  sourceNativeIdPath: NativeId[] | null;
  anchorElement: ExpressionElement;
  anchorNativeIdPath: NativeId[] | null;
  stagedComponents: Component.ComponentMap | null;
};

/**
 * The staged hover, ready for the ordinary validators.
 *
 * `prepared` carries the tree to validate in. `blocked` means the promotion could
 * not be worked out, so the caller should report a drop error and stop.
 *
 * Read `usesProjectedTree` to know which tree you got: false is the live
 * render handed straight back, true is an ephemeral promoted one.
 */
export type ProjectedMoveValidation =
  | PreparedProjectedMoveValidation
  | {kind: 'blocked'};

type ProjectedMoveValidationInput = Omit<
  ProjectedMoveInput,
  'sourceElementId' | 'anchorElementId'
> & {
  sourceElement: ExpressionElement;
  anchorElement: ExpressionElement;
};

const getProjectedValidationComponents = ({
  promotion,
  sourceElement,
  workingRoot,
}: {
  promotion: PromotedSlotDefaultMove;
  sourceElement: ExpressionElement;
  workingRoot: Expression;
}): Component.ComponentMap | null => {
  const destination = promotion.anchorDestination;
  if (destination?.kind !== 'slotDefault' || !promotion.insertion) {
    return null;
  }

  // A new or live source is not in the promoted default, so without staging it
  // here the fallback stages into a render the anchor isn't in and the cycle
  // check never sees the new edge. Mirrors the mover's removal.
  let componentMap = promotion.componentMap;
  const rootElement = Expressions.getElement(workingRoot);
  if (!promotion.detachedSourceElement && rootElement) {
    componentMap = updateOneOfComponents(
      sourceElement,
      rootElement,
      componentMap,
      (renderExpr) => removeElementById(renderExpr, sourceElement.id)
    );
  }

  // Apply the insertion into the ephemeral tree up front so nesting validation
  // can see the element sitting at its destination before the mover ever runs.
  const expression = applySlotDefaultInsertion(
    destination.expression,
    sourceElement,
    promotion.insertion
  );
  return setComponentSlotDefaultExpression({
    componentMap,
    componentType: destination.componentType,
    fieldId: destination.fieldId,
    expression,
  });
};

/**
 * The focused render with a promoted component default materialized.
 *
 * A slot override rides along in its instance's element data, so a render already
 * contains it and an ordinary walk reaches it. A component default does not — it
 * lives on the component, outside every render — so the checks find no parent for
 * it and every before/after beside it dies. Grafting the promoted contents onto
 * the Slot puts them where the walk expects.
 *
 * One graft covers the whole chain: the promotion has already nested each deeper
 * level into this expression as an override on the instance that owns it.
 */
const buildProjectedValidationRoot = (
  componentMap: Component.ComponentMap,
  destination: Extract<
    NonNullable<PromotedSlotDefaultMove['anchorDestination']>,
    {kind: 'slotDefault'}
  >,
  focusedSymbolNativeIdPath: NativeId[]
): Expression | null =>
  getRootElementWithFocusedInstancesExpanded(
    graftSlotContentIntoRender(
      componentMap,
      destination.componentType,
      destination.fieldId,
      destination.expression
    ),
    focusedSymbolNativeIdPath
  );

const preparePromotedMoveValidation = (
  result: Extract<InternalProjectedMovePromotion, {kind: 'promoted'}>,
  {
    sourceElement,
    anchorElement,
    focusedSymbolNativeIdPath,
  }: ProjectedMoveValidationInput,
  workingRoot: Expression
): ProjectedMoveValidation => {
  // The promotion resolves whatever it forked; an untouched live element is
  // still the one the caller handed us.
  let validationSourceElement = result.promotion.detachedSourceElement;
  if (
    !validationSourceElement &&
    sourceElement.id === result.promotion.sourceElementId
  ) {
    validationSourceElement = sourceElement;
  }
  if (!validationSourceElement) return {kind: 'blocked'};

  let validationAnchorElement = result.promotion.anchorElement;
  if (
    !validationAnchorElement &&
    anchorElement.id === result.promotion.anchorElementId
  ) {
    validationAnchorElement = anchorElement;
  }
  if (!validationAnchorElement) return {kind: 'blocked'};

  const stagedComponents = getProjectedValidationComponents({
    promotion: result.promotion,
    sourceElement: validationSourceElement,
    workingRoot,
  });

  // A promotion that landed in a component default is the one case the working
  // root cannot answer for, so that content gets grafted onto its Slot first.
  let root = workingRoot;
  const destination = result.promotion.anchorDestination;
  if (destination?.kind === 'slotDefault') {
    const projectedRoot = buildProjectedValidationRoot(
      result.promotion.componentMap,
      destination,
      focusedSymbolNativeIdPath
    );
    if (projectedRoot) root = projectedRoot;
  }

  return {
    kind: 'prepared',
    usesProjectedTree: true,
    designerState: result.designerState,
    root,
    sourceElement: validationSourceElement,
    sourceNativeIdPath: result.promotion.sourceNativeIdPath,
    anchorElement: validationAnchorElement,
    anchorNativeIdPath: result.promotion.anchorNativeIdPath,
    stagedComponents,
  };
};

/**
 * The focused render, expanded once per map.
 *
 * An ordinary hover reads the same live map at every anchor and a projected one
 * reads a fork that is itself cached per placement, so the expansion is the same
 * answer over and over. It also has to keep its identity: the root is a memo key
 * for the staging downstream, and a fresh one each frame defeats that too.
 */
const expandFocusedRoot = weakMemo((componentMap: Component.ComponentMap) =>
  weakMemo((focusedSymbolNativeIdPath: NativeId[]) =>
    getRootElementWithFocusedInstancesExpanded(
      componentMap,
      focusedSymbolNativeIdPath
    )
  )
);

/**
 * Stages a hover so the ordinary nesting and placement checks can judge it.
 *
 * Slot default content is not in the page render, so the validators have nothing
 * to search. This forks the default into a throwaway tree, puts the source and
 * anchor in it, and hands that back. Nothing is persisted.
 *
 * Live drags pass through with the real render untouched, so callers can call it
 * on every frame without asking whether the drag is projected.
 */
const prepareProjectedMoveValidationUncached = (
  input: ProjectedMoveValidationInput
): ProjectedMoveValidation => {
  const {
    designerState,
    sourceElement,
    sourceNativeIdPath,
    anchorElement,
    anchorNativeIdPath,
    anchorPath,
    focusedSymbolNativeIdPath,
  } = input;
  // Validation needs the drop's destination; preview overrides are outside the focused definition.
  const result = getProjectedMovePromotion(
    {
      designerState,
      sourceElementId: sourceElement.id,
      sourceNativeIdPath,
      anchorElementId: anchorElement.id,
      anchorNativeIdPath,
      anchorPath,
      anchorPosition: input.anchorPosition,
      focusedSymbolNativeIdPath,
    },
    prepareSlotDefaultMoveCommit
  );
  if (result.kind === 'blocked') return result;
  let workingComponentMap = designerState.components;
  if (result.kind === 'promoted') {
    workingComponentMap = result.promotion.componentMap;
  }
  // Promoted content only exists in the ephemeral map, so the validators have to
  // search a root built from that instead of the live render.
  const workingRoot = expandFocusedRoot(workingComponentMap)(
    focusedSymbolNativeIdPath
  );
  if (!workingRoot) return {kind: 'blocked'};

  if (result.kind === 'ordinary') {
    return {
      kind: 'prepared',
      usesProjectedTree: false,
      designerState,
      root: workingRoot,
      sourceElement,
      sourceNativeIdPath,
      anchorElement,
      anchorNativeIdPath,
      stagedComponents: null,
    };
  }

  return preparePromotedMoveValidation(result, input, workingRoot);
};

/**
 * One staging per (state, placement) — the hover that validates and the drop that
 * commits ask the same question, and on canvas every mousemove inside one row
 * asks it again. Elements and state key by reference; native paths compare by
 * value, and Slot anchors key by field id so fresh path objects can hit the cache.
 */
const memoizedPrepareProjectedMoveValidation = weakMemo(
  (designerState: DesignerStateType) =>
    weakMemo((sourceElement: ExpressionElement) =>
      weakMemo((anchorElement: ExpressionElement) =>
        memoize(
          (
            sourceNativeIdPath: NativeId[] | null,
            anchorNativeIdPath: NativeId[] | null,
            anchorSlotFieldId: string | null,
            anchorPosition: ElementMoveDetails['position'],
            focusedSymbolNativeIdPath: NativeId[]
          ) => {
            let anchorPath: ExpressionPath | null = null;
            if (anchorSlotFieldId !== null) {
              anchorPath = [{in: 'Record', at: anchorSlotFieldId}];
            }
            return prepareProjectedMoveValidationUncached({
              designerState,
              sourceElement,
              anchorElement,
              sourceNativeIdPath,
              anchorNativeIdPath,
              anchorPath,
              anchorPosition,
              focusedSymbolNativeIdPath,
            });
          },
          8
        )
      )
    )
);

export const prepareProjectedMoveValidation = (
  input: ProjectedMoveValidationInput
): ProjectedMoveValidation => {
  const anchorSlotFieldId = getTargetedSlotFieldId(input.anchorPath);
  // Only a single-hop Slot path can be rebuilt from the field id.
  if (input.anchorPath && anchorSlotFieldId === null) {
    return prepareProjectedMoveValidationUncached(input);
  }

  return memoizedPrepareProjectedMoveValidation(input.designerState)(
    input.sourceElement
  )(input.anchorElement)(
    input.sourceNativeIdPath,
    input.anchorNativeIdPath,
    anchorSlotFieldId,
    input.anchorPosition,
    input.focusedSymbolNativeIdPath
  );
};

// The promotion already names the container the element was placed into, so the
// landing check reads that one container instead of searching the whole map.
const hasPlacedElement = (
  componentMap: Component.ComponentMap,
  promotion: PromotedSlotDefaultMove,
  plugins: Plugins.PluginsType,
  focusedSymbolNativeIdPath: NativeId[]
): boolean => {
  const {anchorDestination, sourceElementId} = promotion;
  if (anchorDestination?.kind === 'slotDefault') {
    const expression = getEffectiveSlotDefaultExpression(
      plugins,
      componentMap,
      anchorDestination.componentType,
      anchorDestination.fieldId
    );
    return Boolean(
      expression && Expressions.getElementById(expression, sourceElementId)
    );
  }

  if (anchorDestination) {
    const component = componentMap.get(anchorDestination.componentType);
    return Boolean(
      component &&
      Expressions.getElementById(
        Component.getRender(component),
        sourceElementId
      )
    );
  }

  // A drop onto a Slot in the definition being edited lands in that component's
  // defaultData, which no render contains, so a render search would read it as a
  // failed placement and roll a good move back.
  const editingComponentType = getCurrentComponentType(
    componentMap,
    focusedSymbolNativeIdPath
  );
  const editingComponent = editingComponentType
    ? componentMap.get(editingComponentType)
    : null;
  const editingDefaults = editingComponent
    ? Component.getDefaultData(editingComponent)
    : null;
  if (
    editingDefaults &&
    Expressions.getElementById(editingDefaults, sourceElementId)
  ) {
    return true;
  }

  const root = getRootElementWithFocusedInstancesExpanded(
    componentMap,
    focusedSymbolNativeIdPath
  );
  return Boolean(root && Expressions.getElementById(root, sourceElementId));
};

const executeProjectedMove = (
  input: ProjectedMoveInput,
  execute: (
    designerState: DesignerStateType,
    promotion: PromotedSlotDefaultMove | null
  ) => DesignerStateType,
  prepareMove: SlotDefaultMovePreparer
): DesignerStateType => {
  const result = getProjectedMovePromotion(input, prepareMove);
  if (result.kind === 'blocked') {
    return input.designerState.set('stagedComponents', null);
  }
  if (result.kind === 'ordinary') {
    return execute(input.designerState, null);
  }

  const movedState = execute(result.designerState, result.promotion);
  const rollbackState = input.designerState.set('stagedComponents', null);
  // The mover handed back the same map it was given, so nothing landed. Publishing
  // the promotion now would persist a materialized override with nothing in it.
  if (movedState.components === result.promotion.componentMap) {
    return rollbackState;
  }

  const placed = hasPlacedElement(
    movedState.components,
    result.promotion,
    input.designerState.plugins,
    input.focusedSymbolNativeIdPath
  );
  if (!placed) {
    return rollbackState;
  }
  return movedState;
};

const getComponentRoot = (
  componentMap: Component.ComponentMap,
  componentType: Plugins.QualifiedElement | null
) => {
  if (!componentType) return null;
  const component = componentMap.get(componentType);
  if (!component) return null;
  return Expressions.getElement(Component.getRender(component));
};

const applyProjectedPlacementWith = (
  intent: ProjectedPlacementIntent,
  context: ElementMoveContext,
  prepareMove: SlotDefaultMovePreparer
): DesignerStateType => {
  let sourceElementId: string;
  let sourceNativeIdPath: NativeId[] | null;
  let detachedSourceElement: ExpressionElement | null = null;
  if (intent.source.kind === 'new') {
    sourceElementId = intent.source.element.id;
    sourceNativeIdPath = null;
    detachedSourceElement = intent.source.element;
  } else {
    sourceElementId = intent.source.id;
    sourceNativeIdPath = intent.source.nativeIdPath;
  }

  return executeProjectedMove(
    {
      designerState: context.designerState,
      sourceElementId,
      sourceNativeIdPath,
      anchorElementId: intent.destination.id,
      anchorNativeIdPath: intent.destination.nativeIdPath,
      anchorPath: intent.destination.path,
      anchorPosition: intent.destination.position,
      focusedSymbolNativeIdPath: context.uiNodeState.focusedSymbolNativeIdPath,
    },
    (moveDesignerState, promotion) => {
      const destination = promotion?.anchorDestination ?? null;
      const detachedSource =
        detachedSourceElement ?? promotion?.detachedSourceElement ?? null;
      let source: ElementMoveOverrides['source'];
      if (detachedSource) {
        if (intent.source.kind === 'new') {
          source = {kind: 'new', element: detachedSource};
        } else {
          source = {kind: 'detached', element: detachedSource};
        }
      }

      const isDefinitionSlotAnchor = isSlotDefaultDrop({
        designerState: moveDesignerState,
        uiNodeState: context.uiNodeState,
        anchorId: promotion?.anchorElementId ?? intent.destination.id,
        position: intent.destination.position,
      });
      let anchorPath = intent.destination.path ?? undefined;
      if (isDefinitionSlotAnchor) {
        anchorPath = undefined;
      }

      let anchor: ElementMoveOverrides['anchor'];
      if (
        destination?.kind === 'slotDefault' &&
        promotion?.insertion &&
        !isDefinitionSlotAnchor
      ) {
        anchor = {
          kind: 'expression',
          expression: destination.expression,
          insertion: promotion.insertion,
          writeExpression: (componentMap, expression) =>
            setComponentSlotDefaultExpression({
              componentMap,
              componentType: destination.componentType,
              fieldId: destination.fieldId,
              expression,
            }) ?? componentMap,
        };
      } else if (destination) {
        const anchorRoot = getComponentRoot(
          moveDesignerState.components,
          destination.componentType
        );
        if (!anchorRoot) return moveDesignerState;
        anchor = {kind: 'root', root: anchorRoot};
      }
      const moveDetails = {
        id: promotion?.sourceElementId ?? sourceElementId,
        anchorId: promotion?.anchorElementId ?? intent.destination.id,
        position: intent.destination.position,
        anchorPath,
      };
      const moveContext = {...context, designerState: moveDesignerState};
      if (!source && !anchor) {
        return handleElementMoved(moveDetails, moveContext);
      }
      return handleElementMoved(moveDetails, moveContext, {source, anchor});
    },
    prepareMove
  );
};

/**
 * Returns a preview candidate whose components must be staged by the caller.
 *
 * Same transaction as `applyProjectedPlacement` with one difference: while you are
 * editing a component's definition and the pointer is still over projected
 * content, this holds off persisting the source's removal from the shared default.
 * Dragging B out of A's Slot default across other projected targets would
 * otherwise empty A's default on every frame the pointer moved.
 */
export const previewProjectedPlacement = (
  intent: ProjectedPlacementIntent,
  context: ElementMoveContext
): DesignerStateType =>
  applyProjectedPlacementWith(intent, context, promoteSlotDefaultMove);

/**
 * Runs a placement for real, on drop. Handles moves and brand new elements
 * through the one call.
 *
 * Source removal persists now. Dropping B out of A's Slot default and into C's
 * leaves A's default without B for every instance of A, which is the point.
 *
 * Atomic: if the placement does not land, the original state comes back and
 * nothing is written.
 */
export const applyProjectedPlacement = (
  intent: ProjectedPlacementIntent,
  context: ElementMoveContext
): DesignerStateType =>
  applyProjectedPlacementWith(intent, context, prepareSlotDefaultMoveCommit);
